import { useEffect, useMemo, useRef, useState } from "react";
import { WifiOff, RefreshCcw } from "lucide-react";
import type { User } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext";
import {
  AnalyticsUserContext,
  trackOfflineBannerShown,
  trackOfflineBannerToggled,
} from "@/lib/monitoring/analytics";

interface OfflineBannerProps {
  user?: User | null;
}

export function OfflineBanner({ user }: OfflineBannerProps) {
  const { flags } = useFeatureFlags();
  const { isOnline } = useNetworkStatus();
  const [collapsed, setCollapsed] = useState(false);
  const hasReportedShow = useRef(false);

  const userContext: AnalyticsUserContext = useMemo(
    () => ({ id: user?.id, email: user?.email }),
    [user?.id, user?.email]
  );

  useEffect(() => {
    if (!flags.offlineCache) return;

    if (!isOnline && !hasReportedShow.current) {
      trackOfflineBannerShown({ visible: true, collapsed }, userContext);
      hasReportedShow.current = true;
    }

    if (isOnline) {
      hasReportedShow.current = false;
      setCollapsed(false);
    }
  }, [collapsed, flags.offlineCache, isOnline, userContext]);

  if (!flags.offlineCache || isOnline) return null;

  const handleToggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      trackOfflineBannerToggled(next, userContext);
      return next;
    });
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200 text-amber-900" data-testid="offline-banner">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 gap-3">
        <div className="flex items-center gap-2">
          <WifiOff className="h-5 w-5" />
          <div>
            <p className="font-semibold">You're offline</p>
            {!collapsed && (
              <p className="text-sm text-amber-800">
                We'll keep your updates queued and sync them when you're back online.
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleToggle} data-testid="offline-toggle">
            {collapsed ? "Show details" : "Hide"}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => location.reload()}>
            <RefreshCcw className="mr-1 h-4 w-4" />
            Retry connection
          </Button>
        </div>
      </div>
    </div>
  );
}
